import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SocialService } from '../../services/socialService';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../../components/AuthModal/AuthModal';
import { useAuthGate } from '../../hooks/useAuthGate';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import LoadingSkeleton from '../../components/LoadingSkeleton/LoadingSkeleton';
import styles from './Followers.module.css';
import { FoundingCollectorIcon } from '../../components/FoundingCollectorBadge/FoundingCollectorBadge';

function Followers() {
    const { username: rawUsername } = useParams();
    const username = rawUsername?.startsWith('@') ? rawUsername.slice(1) : rawUsername;
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [followers, setFollowers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadFollowers = async () => {
            try {
                setLoading(true);
                setError(null);
                const profile = await SocialService.getPublicProfile(username);
                const data = await SocialService.getFollowers(profile.id);
                setFollowers(data);
            } catch (err) {
                console.error('Failed to load followers:', err);
                setError(err.message || 'Failed to load followers');
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            loadFollowers();
        }
    }, [username]);

    const handleFollow = async (userId, currentlyFollowing) => {
        try {
            if (currentlyFollowing) {
                await SocialService.unfollow(userId);
            } else {
                await SocialService.follow(userId);
            }

            setFollowers(followers.map(follower =>
                follower.id === userId
                    ? { ...follower, isFollowing: !currentlyFollowing }
                    : follower
            ));
        } catch (err) {
            console.error('Failed to update follow status:', err);
        }
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <div className="container">
                    <LoadingSkeleton height="40px" width="200px" />
                    <div style={{ marginTop: 24 }}>
                        {[1, 2, 3].map(i => (
                            <div key={i} style={{ marginBottom: 16 }}>
                                <LoadingSkeleton height="64px" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className="container">
                    <div className={styles.errorState}>
                        <p>{error}</p>
                        <Link to={`/user/${username}`} className={styles.backLink}>← Back to Profile</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <main className={styles.page}>
            <div className="container">
                <div className={styles.header}>
                    <Link to={`/user/${username}`} className={styles.backLink}>← Back</Link>
                    <h1 className={styles.title}>Followers</h1>
                    <p className={styles.subtitle}>@{username}</p>
                </div>

                {followers.length === 0 ? (
                    <div className={styles.emptyState}>
                        <span style={{ fontSize: '2.5rem' }}>👥</span>
                        <p>No followers yet</p>
                    </div>
                ) : (
                    <div className={styles.userList}>
                        {followers.map(follower => (
                            <div key={follower.id} className={styles.userCard}>
                                <Link to={`/user/${follower.username}`} className={styles.userInfo}>
                                    {follower.avatar_url ? (
                                        <img
                                            src={follower.avatar_url}
                                            alt={follower.display_name}
                                            className={styles.avatar}
                                        />
                                    ) : (
                                        <div className={styles.avatarPlaceholder}>
                                            {(follower.display_name || follower.username).charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className={styles.userDetails}>
                                        <p className={styles.displayName}>
                                            {follower.display_name || follower.username}
                                            <FoundingCollectorIcon isFoundingCollector={follower.isFoundingCollector} />
                                        </p>
                                        <p className={styles.username}>@{follower.username}</p>
                                    </div>
                                </Link>
                                {user?.username !== follower.username && (
                                    <button
                                        className={`${styles.followBtn} ${follower.isFollowing ? styles.following : ''}`}
                                        onClick={gateAction(
                                            () => handleFollow(follower.id, follower.isFollowing),
                                            AUTH_MESSAGES.FOLLOW,
                                            {
                                                type: 'FOLLOW_USER',
                                                returnUrl: window.location.pathname + window.location.search + window.location.hash,
                                                userId: follower.id,
                                            },
                                        )}
                                    >
                                        {follower.isFollowing ? 'Following' : follower.followsYou ? 'Follow Back' : 'Follow'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <AuthModal {...authModalProps} />
        </main>
    );
}

export default Followers;
